// The Composer's VIEW-STATE hook: which step is open, how far the operator has been, the drafting
// hand-off, the chosen board axis, and the activation phase. Pure client view state — the drafted
// CONFIGURATION always comes from the projection (G-182) and the console routes NO verdict write
// (P-GATE; the Execute commit belongs to the card). The v2 activation WRITE seam is not in WorkApi
// yet (brief §7 v2), so `activate` re-reads the LANDING through the injected transport and claims
// the live state ONLY when the owning module actually lists the workflow ACTIVE — a missing row or
// a failed re-read lands the honest UNCONFIRMED outcome, never an optimistic success (P-SELF; the
// board hand-off id comes from the owning read, never fabricated). A `busy` flag keeps a
// double-click from double-routing (house GateRow posture).

import { useCallback, useState } from 'react';
import { useWorkApi } from '../api-client.ts';
import type { WorkComposerProjection, WorkLandingProjection, WorkView } from '../projections.ts';
import { COMPOSER_STEPS } from './composer-model.ts';

/** The post-press outcome. `confirmed` is claimed ONLY when the owning landing read lists the
 *  workflow ACTIVE (its id is the board hand-off); anything else — row missing, read failed — is
 *  the honest unconfirmed state (G-182: every displayed state derives from the owning lifecycle). */
export type ActivatedState =
  | { readonly confirmed: true; readonly boardId: string }
  | { readonly confirmed: false };

/** The composer console's view state + intents. */
export interface ComposerStateApi {
  readonly stepIndex: number;
  readonly maxReached: number;
  readonly drafting: boolean;
  readonly activeView: WorkView;
  readonly activated: ActivatedState | null;
  readonly busy: boolean;
  readonly goToStep: (index: number) => void;
  readonly next: () => void;
  readonly back: () => void;
  readonly startDraft: () => void;
  readonly finishDraft: () => void;
  readonly pickView: (view: WorkView) => void;
  readonly activate: () => Promise<void>;
}

/** Clamp a rail index onto the step list. */
function clampStep(index: number): number {
  return Math.max(0, Math.min(COMPOSER_STEPS.length - 1, index));
}

/** The default board axis: the agent's available pick, else the first available option. */
function recommendedView(config: WorkComposerProjection): WorkView {
  const pick =
    config.viewOptions.find((option) => option.recommended && option.available) ??
    config.viewOptions.find((option) => option.available);
  return pick?.view ?? 'kanban';
}

/** Resolve the post-press outcome from the owning landing read: confirmed ONLY when the workflow
 *  is listed ACTIVE (its id is the board hand-off) — anything else is the honest unconfirmed state. */
function confirmActivation(landing: WorkLandingProjection, workflowName: string): ActivatedState {
  const row = landing.definitions.find(
    (definition) => definition.name === workflowName && definition.state === 'active',
  );
  return row !== undefined ? { confirmed: true, boardId: row.id } : { confirmed: false };
}

/** Hold the composer's view state over the injected transport. */
export function useComposerState(config: WorkComposerProjection): ComposerStateApi {
  const api = useWorkApi();
  const [stepIndex, setStepIndex] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const [drafting, setDrafting] = useState(false);
  const [chosenView, setChosenView] = useState<WorkView | null>(null);
  const [activated, setActivated] = useState<ActivatedState | null>(null);
  const [busy, setBusy] = useState(false);

  const goToStep = useCallback((index: number) => {
    const clamped = clampStep(index);
    setStepIndex(clamped);
    setMaxReached((reached) => Math.max(reached, clamped));
  }, []);

  const next = useCallback(() => goToStep(stepIndex + 1), [goToStep, stepIndex]);
  const back = useCallback(() => setStepIndex((index) => Math.max(0, index - 1)), []);
  const startDraft = useCallback(() => setDrafting(true), []);

  // The reveal lands the operator on the Skill step (prototype data-cmp-draft hand-off).
  const finishDraft = useCallback(() => {
    setDrafting(false);
    goToStep(1);
  }, [goToStep]);

  const pickView = useCallback((view: WorkView) => setChosenView(view), []);

  const activate = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      setActivated(confirmActivation(await api.getLanding(), config.workflowName));
    } catch {
      // The owning read failed — claiming "live" would be optimistic (P-SELF); land unconfirmed.
      setActivated({ confirmed: false });
    } finally {
      setBusy(false);
    }
  }, [api, busy, config.workflowName]);

  return {
    stepIndex,
    maxReached,
    drafting,
    activeView: chosenView ?? recommendedView(config),
    activated,
    busy,
    goToStep,
    next,
    back,
    startDraft,
    finishDraft,
    pickView,
    activate,
  };
}
